import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, EyeIcon, KeyIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12">
      <div className="container-custom max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111111] rounded-2xl border border-[#252525] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#F97316] to-[#e8630a] px-8 py-10 text-white">
            <h1 className="text-3xl lg:text-4xl font-display font-bold mb-2">
              Politique de confidentialité
            </h1>
            <p className="text-white/80 text-lg">
              Comment nous protégeons vos données personnelles
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-white/70">
              <ShieldCheckIcon className="h-5 w-5" />
              <span>Dernière mise à jour : 1er mai 2024</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="mb-8 pb-6 border-b border-[#252525]">
              <p className="text-[#AAAAAA] leading-relaxed">
                Chez VADOM, la protection de vos données personnelles est une priorité. 
                Cette politique de confidentialité vous informe sur la manière dont nous collectons, 
                utilisons et protégeons vos informations lorsque vous utilisez notre plateforme.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-[#F97316]" />
                1. Collecte des informations
              </h2>
              <p className="text-[#AAAAAA] leading-relaxed mb-3">
                Nous collectons les informations que vous nous fournissez directement :
              </p>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li><span className="text-white">Informations d'inscription :</span> nom, prénom, email, téléphone</li>
                <li><span className="text-white">Données de profil :</span> photo, adresse, compétences (pour les Smarts)</li>
                <li><span className="text-white">Historique des transactions :</span> réservations, paiements, évaluations</li>
                <li><span className="text-white">Communications :</span> messages avec le support ou les prestataires</li>
                <li><span className="text-white">Données de localisation :</span> pour faciliter les mises en relation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <EyeIcon className="h-5 w-5 text-[#F97316]" />
                2. Utilisation des données
              </h2>
              <p className="text-[#AAAAAA] leading-relaxed mb-3">Vos données sont utilisées pour :</p>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li>Créer et gérer votre compte utilisateur</li>
                <li>Faciliter les mises en relation entre clients et prestataires</li>
                <li>Traiter vos réservations et paiements en toute sécurité</li>
                <li>Vous envoyer des confirmations, notifications et rappels</li>
                <li>Améliorer nos services et personnaliser votre expérience</li>
                <li>Prévenir la fraude et assurer la sécurité de la plateforme</li>
                <li>Respecter nos obligations légales et réglementaires</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <KeyIcon className="h-5 w-5 text-[#F97316]" />
                3. Protection des données
              </h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles 
                appropriées pour protéger vos données contre tout accès non autorisé, perte, 
                destruction ou divulgation. Cela comprend le chiffrement des données, 
                les pare-feu, le contrôle d'accès strict et la formation de notre personnel.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">4. Partage des informations</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Vos informations sont partagées uniquement dans les cas suivants :
              </p>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc mt-2">
                <li><span className="text-white">Avec les prestataires :</span> les informations nécessaires à la prestation (nom, adresse, description du besoin)</li>
                <li><span className="text-white">Avec nos partenaires de paiement :</span> pour traiter les transactions financières</li>
                <li><span className="text-white">Obligations légales :</span> si la loi nous y contraint</li>
                <li><span className="text-white">Avec votre consentement :</span> pour toute autre utilisation spécifique</li>
              </ul>
              <p className="text-[#AAAAAA] leading-relaxed mt-3">
                Nous ne vendons pas vos données personnelles à des tiers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">5. Vos droits</h2>
              <p className="text-[#AAAAAA] leading-relaxed mb-3">
                Conformément à la réglementation applicable, vous disposez des droits suivants :
              </p>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li><span className="text-white">Droit d'accès :</span> connaître les données que nous détenons sur vous</li>
                <li><span className="text-white">Droit de rectification :</span> modifier vos informations inexactes</li>
                <li><span className="text-white">Droit à l'effacement :</span> demander la suppression de vos données</li>
                <li><span className="text-white">Droit d'opposition :</span> refuser certains traitements de données</li>
                <li><span className="text-white">Droit à la portabilité :</span> récupérer vos données dans un format exploitable</li>
              </ul>
              <p className="text-[#AAAAAA] leading-relaxed mt-3">
                Pour exercer ces droits, contactez-nous à <a href="mailto:vadomxpress@gmail.com" className="text-[#F97316] hover:underline">vadomxpress@gmail.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">6. Cookies</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Nous utilisons des cookies pour améliorer votre expérience sur notre plateforme. 
                Les cookies nous aident à mémoriser vos préférences, analyser l'utilisation du site 
                et personnaliser le contenu. Vous pouvez gérer vos préférences via les paramètres 
                de votre navigateur. Pour plus d'informations, consultez notre 
                <Link to="/legal/cookies" className="text-[#F97316] hover:underline ml-1">Politique des cookies</Link>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">7. Conservation des données</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Nous conservons vos données personnelles aussi longtemps que nécessaire pour 
                fournir nos services, respecter nos obligations légales, résoudre les litiges 
                et appliquer nos conditions. En général, les données sont conservées pendant 
                5 ans après la dernière activité sur votre compte.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">8. Transfert international des données</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Vos données peuvent être transférées et stockées sur des serveurs situés en dehors 
                de votre pays de résidence. Nous prenons toutes les garanties nécessaires pour 
                assurer un niveau de protection adéquat de vos données, conformément à la législation 
                applicable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">9. Modifications de la politique</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Nous pouvons mettre à jour cette politique de confidentialité occasionnellement. 
                Nous vous informerons de tout changement substantiel par notification sur la plateforme 
                ou par email. La date de la dernière mise à jour est indiquée en haut de cette page.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">10. Contact</h2>
              <div className="bg-[#0a0a0a] rounded-xl p-5 space-y-2">
                <p className="text-[#AAAAAA]">Pour toute question concernant cette politique ou vos données :</p>
                <p className="text-white">📧 Email : <a href="mailto:vadomxpress@gmail.com" className="text-[#F97316] hover:underline">vadomxpress@gmail.com</a></p>
                <p className="text-white">📍 Adresse : VADOM SAS, Lomé, Togo</p>
                <p className="text-white">👤 DPO : Monsieur le Délégué à la Protection des Données</p>
              </div>
            </section>

            {/* CTA */}
            <div className="mt-10 pt-6 border-t border-[#252525] text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Une question sur vos données ?</h3>
              <p className="text-[#AAAAAA] mb-4">Notre équipe est à votre disposition.</p>
              <Link to="/contact" className="inline-block bg-[#F97316] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#e8630a] transition">
                Nous contacter
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;